/**
 * Spec-compliant incremental Server-Sent-Events parser (WHATWG HTML §9.2).
 *
 * Handles what the old `buffer.split('\n\n')` loop did not:
 *  - CRLF and bare-CR line endings (proxies/Cloudflare may rewrite them),
 *    including a CRLF pair split across two network chunks;
 *  - multi-line `data:` fields (joined with "\n");
 *  - `: comment` lines (heartbeats) which are surfaced separately and never
 *    reach the app as events;
 *  - `event:`, `id:` and `retry:` fields, with or without the optional space;
 *  - frames fragmented at ANY byte boundary (feed it arbitrary string chunks;
 *    use a streaming TextDecoder so multi-byte characters are not split).
 */
export interface SseFrame {
  /** `event:` field; defaults to "message" per spec. */
  event: string
  /** Joined `data:` lines. */
  data: string
  /** Last event id seen on this stream (sticky, per spec). */
  id?: string
  /** `retry:` in ms, when present on this frame. */
  retry?: number
}

export interface SseParserHandlers {
  onFrame: (frame: SseFrame) => void
  /** Called for each `:` comment line (heartbeat / keepalive). */
  onComment?: (text: string) => void
}

export class SseParser {
  private line = ''
  private skipLf = false
  private data: string[] = []
  private event = ''
  private retry: number | undefined
  private hasFields = false
  private lastId: string | undefined
  private bomChecked = false

  constructor(private readonly handlers: SseParserHandlers) {}

  /** Last event id observed (for resume / diagnostics). */
  get lastEventId(): string | undefined {
    return this.lastId
  }

  feed(chunk: string): void {
    if (!chunk) return
    if (!this.bomChecked) {
      this.bomChecked = true
      if (chunk.charCodeAt(0) === 0xfeff) chunk = chunk.slice(1)
    }
    for (let i = 0; i < chunk.length; i += 1) {
      const ch = chunk[i]
      if (this.skipLf) {
        this.skipLf = false
        if (ch === '\n') continue // second half of a CRLF split across chunks
      }
      if (ch === '\r') {
        this.flushLine()
        this.skipLf = true
      } else if (ch === '\n') {
        this.flushLine()
      } else {
        this.line += ch
      }
    }
  }

  /** End of stream. A trailing partial frame without a blank line is discarded (spec). */
  end(): void {
    this.line = ''
    this.skipLf = false
    this.resetFrame()
  }

  private flushLine(): void {
    const line = this.line
    this.line = ''
    this.processLine(line)
  }

  private resetFrame(): void {
    this.data = []
    this.event = ''
    this.retry = undefined
    this.hasFields = false
  }

  private processLine(line: string): void {
    if (line === '') {
      if (this.hasFields && (this.data.length > 0 || this.event)) {
        this.handlers.onFrame({
          event: this.event || 'message',
          data: this.data.join('\n'),
          id: this.lastId,
          retry: this.retry,
        })
      }
      this.resetFrame()
      return
    }
    if (line.startsWith(':')) {
      this.handlers.onComment?.(line.slice(1).replace(/^ /, ''))
      return
    }
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    switch (field) {
      case 'data':
        this.data.push(value)
        this.hasFields = true
        break
      case 'event':
        this.event = value
        this.hasFields = true
        break
      case 'id':
        if (!value.includes('\0')) this.lastId = value
        break
      case 'retry':
        if (/^\d+$/.test(value)) this.retry = Number(value)
        break
      default:
        break
    }
  }
}
